/**
 * Triagem das divergências da nota × OC: o MESMO agrupamento no Slack e no WMS.
 *
 * Pedido do Paulo, 01/11/2026: "o usuário precisa bater o olho e saber o que
 * triar". Cada tipo tem cabeçalho, cor e pergunta próprios:
 *
 *  - SKU NÃO APROVADO: o SKU não está na OC ou não passou pelo pré-pedido
 *    aprovado. Pede autorização. Mostra produto, fornecedor, quantidade e NF.
 *  - QUANTIDADE: veio mais do que o saldo do pedido.
 *  - PREÇO ≠ OC: SKU e quantidade batem, o preço é que difere do aprovado.
 *  - OC NÃO CONFERE: sem OC, OC de outro fornecedor/empresa ou encerrada.
 *  - SEM CADASTRO: o ERP não acha o produto.
 *
 * O TOTAL em R$ só aparece quando TODA divergência aberta é de preço: com SKU
 * ou quantidade fora, a soma mistura coisas diferentes e esconde o principal.
 */

export type GrupoDivergencia = 'oc' | 'cadastro' | 'sku_nao_aprovado' | 'quantidade' | 'preco';

export interface DefinicaoGrupo {
  /** Cabeçalho, em caixa alta no Slack. */
  titulo: string;
  /** Rótulo do selo (lista e gaveta). */
  curto: string;
  /** Uma frase: o que o tipo significa. */
  explica: string;
  /** O que fazer. */
  acao: string;
  /** Emoji do Slack. */
  emoji: string;
}

/** Na ordem da triagem: o que impede conferir o resto vem primeiro. */
export const GRUPOS_DIVERGENCIA: Record<GrupoDivergencia, DefinicaoGrupo> = {
  oc: {
    titulo: 'OC não confere',
    curto: 'OC',
    explica: 'A nota não diz a OC, ou a OC é de outro fornecedor ou empresa, ou está encerrada.',
    acao: 'Responda `oc 12345` com a OC certa',
    emoji: ':link:',
  },
  cadastro: {
    titulo: 'Produto sem cadastro',
    curto: 'Sem cadastro',
    explica: 'O ERP não acha o produto nem pelo EAN nem pela referência do fornecedor.',
    acao: 'Associar o SKU no WMS',
    emoji: ':card_index_dividers:',
  },
  // Os três formatos aprovados pelo Paulo em 01/11/2026 (SKU, preço e quantidade contra o pré-pedido aprovado).
  sku_nao_aprovado: {
    titulo: 'SKU não passou pela aprovação do pré-pedido',
    curto: 'SKU sem aprovação',
    explica: 'Sem preço aprovado para conferir a margem · não passou pela validação de DOH · precisa de nova aprovação.',
    acao: 'Quem aprova DOH: `aceitar <motivo>` ou `recusar <motivo>`',
    emoji: ':no_entry:',
  },
  quantidade: {
    titulo: 'Quantidade acima do aprovado no pré-pedido',
    curto: 'Quantidade acima',
    explica: 'O excedente não passou pela aprovação do pré-pedido e pode furar o DOH aprovado: precisa de validação de DOH.',
    acao: 'Quem aprova DOH: `aceitar <motivo>` (fica o excedente), `recusar <motivo>` ou pedir a devolução do excedente',
    emoji: ':package:',
  },
  preco: {
    titulo: 'Preço acima do aprovado no pré-pedido',
    curto: 'Preço acima',
    explica: 'SKU e quantidade batem; o preço da nota passou do aprovado no pré-pedido.',
    acao: 'Quem aprova DOH: `aceitar <motivo>`, `recusar <motivo>` ou pedir correção ao fornecedor',
    emoji: ':moneybag:',
  },
};

const ORDEM: GrupoDivergencia[] = ['oc', 'cadastro', 'sku_nao_aprovado', 'quantidade', 'preco'];

export function grupoDaRegra(regra: string): GrupoDivergencia {
  switch (regra) {
    case 'preco':
      return 'preco';
    case 'quantidade':
      return 'quantidade';
    case 'fora_pre_pedido':
    case 'item_fora_oc':
      return 'sku_nao_aprovado';
    case 'produto':
      return 'cadastro';
    default:
      return 'oc';
  }
}

/** As divergências por tipo, na ordem da triagem (só os grupos que têm alguma). */
export function agruparDivergencias<T extends { regra: string }>(lista: readonly T[]): { grupo: GrupoDivergencia; itens: T[] }[] {
  return ORDEM.map((grupo) => ({ grupo, itens: lista.filter((x) => grupoDaRegra(x.regra) === grupo) })).filter((g) => g.itens.length);
}

/** Total em R$ só quando tudo é preço (SKU e quantidade batem). */
export function mostraTotalDivergencia(lista: readonly { regra: string }[]): boolean {
  return lista.length > 0 && lista.every((x) => x.regra === 'preco');
}

const real = (v: string): number => Number(v.replace(/\./g, '').replace(',', '.'));

/**
 * Os preços da mensagem de preço: Nota, Pré-pedido (o aprovado) e OC hoje.
 * Lê o formato novo ("Nota R$ X/un · Pré-pedido N R$ Y/un · OC N hoje R$ Z/un")
 * e o antigo ("R$ X por unidade, a OC N diz R$ Y"), que continua nas notas velhas.
 */
export function precosDaMensagem(msg: string): { nota: number; pre: number | null; prePedido: string | null; oc: number | null; ref: number } | null {
  const novo = /Nota R\$\s*([\d.,]+)\/un · (?:Pré-pedido (\d+) R\$\s*([\d.,]+)\/un · OC \d+ hoje R\$\s*([\d.,]+)\/un|OC \d+ R\$\s*([\d.,]+)\/un)/.exec(msg);
  if (novo) {
    const pre = novo[3] ? real(novo[3]) : null;
    const oc = novo[4] ? real(novo[4]) : novo[5] ? real(novo[5]) : null;
    return { nota: real(novo[1]!), pre, prePedido: novo[2] ?? null, oc, ref: Math.min(...[pre, oc].filter((x): x is number => x != null)) };
  }
  const velho = /R\$\s*([\d.,]+) por unidade, a OC \d+ diz R\$\s*([\d.,]+)/.exec(msg);
  return velho ? { nota: real(velho[1]!), pre: null, prePedido: null, oc: real(velho[2]!), ref: real(velho[2]!) } : null;
}

/** "São R$ 70,00 a mais em 100 un" → 70. */
export function aMaisDaMensagem(msg: string): number | null {
  const m = /São R\$\s*([\d.,]+) a mais/.exec(msg);
  return m ? real(m[1]!) : null;
}

/** Nota × saldo do pedido, da divergência de quantidade. */
export function quantidadesDaDivergencia(x: { veio: string | null; esperado: string | null; mensagem: string }): { nota: number; saldo: number } | null {
  const m = /Nota ([\d.,]+) un · saldo do pedido (-?[\d.,]+) un/.exec(x.mensagem) ?? /a nota traz ([\d.,]+) e a OC \d+ tem saldo de (-?[\d.,]+)/.exec(x.mensagem);
  if (m) return { nota: real(m[1]!), saldo: real(m[2]!) };
  const n = /(-?[\d.,]+)/.exec(x.veio ?? '');
  const s = /(-?[\d.,]+)/.exec(x.esperado ?? '');
  return n && s ? { nota: real(n[1]!), saldo: real(s[1]!) } : null;
}

/** "Item 2 · SKU 32067 (FOGAO GAS 4Q…)" → { sku, descricao }. */
export function itemDaMensagem(msg: string): { sku: number | null; descricao: string | null } {
  const m = /^Item \d+ (?:· SKU (\d+) )?\(([^)]*)\)/.exec(msg);
  return { sku: m?.[1] ? Number(m[1]) : null, descricao: m?.[2]?.trim() || null };
}

/**
 * O motivo do SKU sem aprovação, como frase (formato de 01/11/2026):
 *  - fora da OC: "não está na OC 43504 nem no pré-pedido 9352";
 *  - fora do pré-pedido e incluído depois: "incluído na OC 43496 depois da aprovação do pré-pedido 9347".
 * `incluidoDepois` diz se o aviso "alterado após a aprovação" vale.
 */
export function motivoSkuNaoAprovado(
  x: { regra: string; mensagem: string; ocSugerida?: number | null },
  prePedido?: number | null,
): { texto: string; incluidoDepois: boolean } {
  if (x.regra === 'fora_pre_pedido') {
    const oc = /a OC (\d+) tem o produto/.exec(x.mensagem)?.[1];
    const pre = /pré-pedido (\d+)/.exec(x.mensagem)?.[1] ?? (prePedido != null ? String(prePedido) : null);
    const depois = x.mensagem.includes('incluído na OC depois');
    return {
      texto: depois
        ? `incluído na OC${oc ? ` ${oc}` : ''} depois da aprovação do pré-pedido${pre ? ` ${pre}` : ''}`
        : `está na OC${oc ? ` ${oc}` : ''}, mas não no pré-pedido${pre ? ` ${pre}` : ''} aprovado`,
      incluidoDepois: depois,
    };
  }
  const oc = /não está na OC (\d+)/.exec(x.mensagem)?.[1];
  return {
    texto:
      `não está na OC${oc ? ` ${oc}` : ''}${prePedido != null ? ` nem no pré-pedido ${prePedido}` : ''}` +
      (x.ocSugerida ? ` (está na OC ${x.ocSugerida})` : ''),
    incluidoDepois: false,
  };
}

/**
 * A conta da quantidade, da mensagem da divergência: aprovado (pré-pedido, ou a
 * OC sem pré-pedido), já recebido, saldo, nota e o excedente.
 */
export function contaDaQuantidade(x: { veio: string | null; esperado: string | null; mensagem: string }): {
  aprovado: number | null;
  jaRecebido: number;
  saldo: number;
  nota: number;
  excedente: number;
} | null {
  const q = quantidadesDaDivergencia(x);
  if (!q) return null;
  const pre = /pré-pedido \d+ ([\d.,]+) un/.exec(x.mensagem)?.[1];
  const oc = /OC \d+ (?:hoje )?([\d.,]+) un/.exec(x.mensagem)?.[1];
  const ja = /já recebido ([\d.,]+)/.exec(x.mensagem)?.[1];
  return {
    aprovado: pre ? real(pre) : oc ? real(oc) : null,
    jaRecebido: ja ? real(ja) : 0,
    saldo: Math.max(0, q.saldo),
    nota: q.nota,
    excedente: q.nota - Math.max(0, q.saldo),
  };
}
